import React from "react";
import { fixedDecimals } from "../../../../helpers/helpers";
import { useTranslation } from "react-i18next";

const ShipmentChargesTable = ({
  data,
  taxCharges,
  additionalCharges,
  newCharge,
  setNewTaxModal,
  setAdditionalChargeModal,
  ChargeDifference,
  newChargeData,
  newTaxData,
  additionalChargesData,
  finaltotaldifference,
  handleChargeChange,
  handleAdditionalChargeChange,
  finalTotal,
  finaltotaldata,
  handleTaxChange,
  modifyInvoice,
  findTaxChargeByNameEn,
  findAdditionalChargeByNameEn,
  calculateServiceChargePercentage,
}) => {
  const { t } = useTranslation("finances");
  const hasInsurance = !!data?.insurance;
  const hasServiceCharge = parseFloat(data?.service_charge ?? "0") > 0;
  const hasShipmentCharges = !!data?.shipment_charges;
  const hasNoQuoteCharges =
    Array.isArray(data?.quoteCharges) && data?.quoteCharges?.length === 0;
  // Referral markup is a shipment-level server field, like service_charge.
  // Display only: it is in no payload and in none of the totals here. The
  // rate comes from the reward's markup_snapshot.
  const referralMarkup = Number(data?.referral_markup || 0);
  const hasReferralMarkup = referralMarkup > 0;
  const referralRewards = Array.isArray(data?.referral_rewards)
    ? data.referral_rewards
    : [];
  const referralReward = referralRewards[0] ?? null;
  const referralSnapshot = parseFloat(referralReward?.markup_snapshot);
  const referralRate =
    referralSnapshot > 0
      ? referralReward?.referral?.markup_type === "$"
        ? t("invoice.referral.flatRate", {
            amount: referralSnapshot.toFixed(2),
          })
        : `${referralSnapshot} %`
      : null;

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-max mb-2 text-[10px] md:text-xxs lg:text-xs xl:text-sm font-medium">
        <thead>
          <tr>
            <th className="p-1 text-center font-semibold">#</th>
            <th className="p-1 text-center font-semibold">
              {t("invoice.charges.base")}
            </th>
            <th className="p-1 text-center font-semibold">
              {t("invoice.charges.fuel")}
            </th>
            <th className="p-1 text-center font-semibold">
              {t("invoice.charges.freight")}
            </th>
            <th className="p-1 text-center font-semibold">
              {t("invoice.charges.signature")}
            </th>
            <th className="p-1 text-center font-semibold">
              {t("invoice.charges.residential")}
            </th>
            <th className="p-1 text-center font-semibold">
              {t("invoice.charges.pickup")}
            </th>
            {hasInsurance && (
              <th className="p-1 text-center font-semibold">
                {t("invoice.charges.insurance")}
              </th>
            )}
            {hasShipmentCharges &&
              additionalCharges?.map((charge, index) => (
                <th className="p-1 text-center font-semibold" key={index}>
                  {charge?.NameEn}
                </th>
              ))}
            <th className="p-1 text-center font-semibold">
              {t("invoice.charges.subTotal")}
            </th>
            {hasShipmentCharges &&
              taxCharges?.map((tax, index) => (
                <th className="p-1 text-center font-semibold" key={index}>
                  {tax?.NameEn}
                </th>
              ))}
            {newCharge && (
              <th className="p-1 text-center font-medium">
                <button
                  className="text-xxs font-medium bg-brand text-white px-2 py-1 rounded hover:bg-brand"
                  onClick={() => setNewTaxModal(true)}
                >
                  {t("invoice.charges.newTax")}
                </button>
                <button
                  className="text-xxs font-medium bg-state-success text-white px-2 py-1 rounded ml-2 hover:bg-state-success"
                  onClick={() => setAdditionalChargeModal(true)}
                >
                  {t("invoice.charges.newCharge")}
                </button>
              </th>
            )}
            {hasServiceCharge && (
              <th className="p-1 text-center font-semibold">
                {t("invoice.charges.serviceCharge")}
              </th>
            )}
            {hasReferralMarkup && (
              <th className="p-1 text-center font-semibold">
                {t("invoice.charges.referralMarkup")}
              </th>
            )}
            {hasNoQuoteCharges && hasServiceCharge && (
              <th className="p-1 text-center font-semibold">
                {t("invoice.charges.carrierTotal")}
              </th>
            )}
            <th className="p-1 text-center font-semibold">
              {t("invoice.charges.total")}
            </th>
          </tr>
        </thead>
        <tbody>
          {data?.shipment_charges?.map((charge, index) => (
            <tr key={index}>
              <td className="p-1 text-center">{index + 1}</td>
              {[
                "base_price",
                "fuel_charge",
                "freight_charge",
                "signature_charge",
                "residential_charge",
                "PickupCharge",
                ...(hasInsurance ? ["insurance_charge"] : []), // Ensuring conditional inclusion
              ].map((key) => (
                <td key={key} className="p-1 text-center">
                  {charge?.[key] && charge?.[key] !== 0 ? charge?.[key] : "-"}
                </td>
              ))}
              {hasShipmentCharges &&
                additionalCharges?.map((charge, idx) => (
                  <td className="p-1 text-center" key={idx}>
                    {findAdditionalChargeByNameEn(charge?.NameEn, index)
                      ?.Charge ?? "-"}
                  </td>
                ))}

              <td className="p-1 text-center">
                {charge?.sub_total && charge?.sub_total !== 0
                  ? charge?.sub_total
                  : "-"}
              </td>
              {hasShipmentCharges &&
                taxCharges?.map((tax, idx) => (
                  <td className="p-1 text-center" key={idx}>
                    {findTaxChargeByNameEn(tax?.NameEn, index)?.Charge ?? "-"}
                  </td>
                ))}
              {newCharge && <td className="p-1"></td>}
              {hasServiceCharge && (
                <td className="p-1 text-center">
                  {(() => {
                    const serviceCharge = Number(data?.service_charge || 0);

                    if (index !== 0 || serviceCharge <= 0) return "-";

                    const markup =
                      data?.markup > 0
                        ? parseFloat(data.markup)
                        : calculateServiceChargePercentage(
                            charge?.total,
                            serviceCharge,
                          );

                    return `${serviceCharge.toFixed(2)} (${markup} %)`;
                  })()}
                </td>
              )}
              {hasReferralMarkup && (
                <td className="p-1 text-center">
                  {index !== 0
                    ? "-"
                    : referralRate
                      ? `${referralMarkup.toFixed(2)} (${referralRate})`
                      : referralMarkup.toFixed(2)}
                </td>
              )}
              {hasNoQuoteCharges && hasServiceCharge && (
                <td className="p-1 text-center">
                  {charge?.total && charge?.total !== 0
                    ? fixedDecimals(
                        parseFloat(charge?.total) -
                          parseFloat(data?.service_charge ?? "0"),
                      )
                    : "-"}
                </td>
              )}
              <td className="p-1 text-center">
                {charge?.total && charge?.total !== 0
                  ? fixedDecimals(charge?.total)
                  : "-"}
              </td>
            </tr>
          ))}
          {newCharge && (
            <>
              <tr>
                <td className="p-1 text-center shadow rounded">
                  {t("invoice.charges.diff")}
                </td>
                {[
                  "base_price",
                  "fuel_charge",
                  "freight_charge",
                  "signature_charge",
                  "residential_charge",
                  "PickupCharge",
                  ...(hasInsurance ? ["insurance_charge"] : []), // Ensuring conditional inclusion
                ].map((key) => (
                  <td key={key} className="p-1 text-center">
                    <ChargeDifference
                      previousCharge={0}
                      newCharge={newChargeData?.[key]}
                    />
                  </td>
                ))}
                {hasShipmentCharges &&
                  additionalCharges?.map((charge, idx) => (
                    <td className="p-1 text-center" key={idx}>
                      <ChargeDifference
                        previousCharge={0}
                        newCharge={additionalChargesData[charge?.NameEn]}
                      />
                    </td>
                  ))}
                <td className="p-1 text-center">
                  <ChargeDifference
                    previousCharge={0}
                    newCharge={newChargeData?.sub_total}
                  />
                </td>
                {hasShipmentCharges &&
                  taxCharges?.map((tax, idx) => (
                    <td className="p-1 text-center" key={idx}>
                      <ChargeDifference
                        previousCharge={0}
                        newCharge={newTaxData[tax?.NameEn]}
                      />
                    </td>
                  ))}
                {newCharge && <td className="p-1"></td>}
                {hasServiceCharge && <td className="p-1 text-center"></td>}
                {hasReferralMarkup && <td className="p-1 text-center"></td>}
                {hasNoQuoteCharges && hasServiceCharge && (
                  <td className="p-1 text-center"></td>
                )}
                <td className="p-1 text-center">
                  <ChargeDifference
                    previousCharge={0}
                    newCharge={finaltotaldifference}
                  />
                </td>
              </tr>

              <tr>
                <td className="p-1 text-center">
                  {data?.shipment_charges?.length + 1}
                </td>
                {[
                  "base_price",
                  "fuel_charge",
                  "freight_charge",
                  "signature_charge",
                  "residential_charge",
                  "PickupCharge",
                  ...(hasInsurance ? ["insurance_charge"] : []), // Ensuring conditional inclusion
                ].map((key) => (
                  <td key={key} className="p-1 text-center">
                    <input
                      type="text"
                      className="w-14 px-1 py-0.5 border border-ui-border text-xs text-center rounded focus:outline-none focus:ring focus:ring-brand"
                      onChange={(e) =>
                        handleChargeChange(newCharge?.data?.[key], e)
                      }
                      name={key}
                    />
                  </td>
                ))}
                {hasShipmentCharges &&
                  additionalCharges?.map((charge, idx) => (
                    <td className="p-1 text-center" key={idx}>
                      <input
                        type="text"
                        className="w-14 px-1 py-0.5 border border-ui-border text-xs text-center rounded focus:outline-none focus:ring focus:ring-brand"
                        value={additionalChargesData[charge.NameEn]}
                        name={charge.NameEn}
                        onChange={(e) =>
                          handleAdditionalChargeChange(charge.Charge, e)
                        }
                      />
                    </td>
                  ))}
                <td className="p-1 text-center">
                  <input
                    type="text"
                    className="w-14 px-1 py-0.5 border border-ui-border text-xs text-center rounded focus:outline-none focus:ring focus:ring-brand"
                    onChange={(e) =>
                      handleChargeChange(newCharge?.data?.sub_total, e)
                    }
                    name="sub_total"
                  />
                </td>
                {hasShipmentCharges &&
                  taxCharges?.map((tax, idx) => (
                    <td className="p-1 text-center" key={idx}>
                      <input
                        type="text"
                        className="w-14 px-1 py-0.5 border border-ui-border text-xs text-center rounded focus:outline-none focus:ring focus:ring-brand"
                        value={newTaxData[tax.NameEn]}
                        name={tax.NameEn}
                        onChange={(e) => handleTaxChange(tax.Charge, e)}
                      />
                    </td>
                  ))}
                {newCharge && <td className="p-1"></td>}
                {hasServiceCharge && <td className="p-1"></td>}
                {hasReferralMarkup && <td className="p-1"></td>}
                {hasNoQuoteCharges && hasServiceCharge && (
                  <td className="p-1"></td>
                )}
                <td className="p-1"></td>
              </tr>

              <tr>
                <td className="p-1 text-center shadow rounded">
                  {t("invoice.charges.total")}
                </td>
                {[
                  "base_price",
                  "fuel_charge",
                  "freight_charge",
                  "signature_charge",
                  "residential_charge",
                  "PickupCharge",
                  ...(hasInsurance ? ["insurance_charge"] : []), // Ensuring conditional inclusion
                ].map((key) => (
                  <td key={key} className="p-1 text-center">
                    {finalTotal?.[key] ?? "-"}
                  </td>
                ))}
                {hasShipmentCharges &&
                  additionalCharges?.map((charge, idx) => (
                    <th className="p-1 text-center" key={idx}>
                      {finalTotal[charge.NameEn] ?? "-"}
                    </th>
                  ))}
                <td className="p-1 text-center">
                  {finalTotal?.sub_total ?? "-"}
                </td>
                {hasShipmentCharges &&
                  taxCharges?.map((tax, idx) => (
                    <th className="p-1 text-center" key={idx}>
                      {finalTotal[tax.NameEn] ?? "-"}
                    </th>
                  ))}
                {newCharge && <td className="p-1"> </td>}
                {hasServiceCharge && <td className="p-1 text-center"></td>}
                {hasReferralMarkup && <td className="p-1 text-center"></td>}
                {hasNoQuoteCharges && hasServiceCharge && (
                  <td className="p-1 text-center">
                    {finaltotaldata
                      ? fixedDecimals(
                          parseFloat(finaltotaldata) -
                            parseFloat(data?.service_charge ?? "0"),
                        )
                      : "-"}
                  </td>
                )}
                <td className="p-1 text-center">
                  {finaltotaldata ? fixedDecimals(finaltotaldata) : "-"}
                </td>
              </tr>
            </>
          )}
          {modifyInvoice && (
            <>
              <tr>
                <td className="p-1 text-center">
                  {data?.shipment_charges?.length + 1}
                </td>
                {[
                  "base_price",
                  "fuel_charge",
                  "freight_charge",
                  "signature_charge",
                  "residential_charge",
                  "PickupCharge",
                  ...(hasInsurance ? ["insurance_charge"] : []), // Ensuring conditional inclusion
                ].map((key) => (
                  <td key={key} className="p-1 text-center">
                    <input
                      type="text"
                      className="w-14 px-1 py-0.5 border border-ui-border text-xs text-center rounded focus:outline-none focus:ring focus:ring-brand"
                      onChange={(e) =>
                        handleChargeChange(modifyInvoice?.data?.[key], e)
                      }
                      value={newChargeData?.[key]}
                      name={key}
                    />
                  </td>
                ))}
                {hasShipmentCharges &&
                  additionalCharges?.map((charge, idx) => (
                    <td key={idx} className="p-1 text-center">
                      <input
                        type="text"
                        className="w-14 px-1 py-0.5 border border-ui-border text-xs text-center rounded focus:outline-none focus:ring focus:ring-brand"
                        value={
                          additionalChargesData[charge.NameEn] ?? charge.Charge
                        }
                        name={charge.NameEn}
                        onChange={(e) =>
                          handleAdditionalChargeChange(charge.Charge, e)
                        }
                      />
                    </td>
                  ))}
                <td className="p-1 text-center">
                  <input
                    type="text"
                    className="w-14 px-1 py-0.5 border border-ui-border text-xs text-center rounded focus:outline-none focus:ring focus:ring-brand"
                    onChange={(e) =>
                      handleChargeChange(modifyInvoice?.data?.sub_total, e)
                    }
                    value={newChargeData?.sub_total}
                    name="sub_total"
                  />
                </td>
                {hasShipmentCharges &&
                  taxCharges?.map((tax, idx) => (
                    <td key={idx} className="p-1 text-center">
                      <input
                        type="text"
                        className="w-14 px-1 py-0.5 border border-ui-border text-xs text-center rounded focus:outline-none focus:ring focus:ring-brand"
                        value={newTaxData[tax.NameEn]}
                        name={tax.NameEn}
                        onChange={(e) => handleTaxChange(tax.Charge, e)}
                      />
                    </td>
                  ))}
                {hasServiceCharge && <td className="p-1 text-center"></td>}
                {hasReferralMarkup && <td className="p-1 text-center"></td>}
                {hasNoQuoteCharges && hasServiceCharge && (
                  <td className="p-1 text-center"></td>
                )}
                <td className="p-1 text-center">
                  <input
                    type="text"
                    className="w-14 px-1 py-0.5 border border-ui-border text-xs text-center rounded focus:outline-none focus:ring focus:ring-brand"
                    onChange={(e) =>
                      handleChargeChange(modifyInvoice?.data?.total, e)
                    }
                    value={newChargeData?.total ?? 0}
                    name="total"
                  />
                </td>
              </tr>

              <tr>
                <td className="p-1 text-center shadow rounded">
                  {t("invoice.charges.total")}
                </td>
                {[
                  "base_price",
                  "fuel_charge",
                  "freight_charge",
                  "signature_charge",
                  "residential_charge",
                  "PickupCharge",
                  ...(hasInsurance ? ["insurance_charge"] : []), // Ensuring conditional inclusion
                ].map((key) => (
                  <td key={key} className="p-1 text-center">
                    {finalTotal?.[key] ?? "-"}
                  </td>
                ))}
                {hasShipmentCharges &&
                  additionalCharges?.map((charge, idx) => (
                    <td className="p-1 text-center" key={idx}>
                      {finalTotal[charge.NameEn] ?? "-"}
                    </td>
                  ))}
                <td className="p-1 text-center">
                  {finalTotal?.sub_total ?? "-"}
                </td>
                {hasShipmentCharges &&
                  taxCharges?.map((tax, idx) => (
                    <td className="p-1 text-center" key={idx}>
                      {finalTotal[tax.NameEn] ?? "-"}
                    </td>
                  ))}
                {hasServiceCharge && <td className="p-1 text-center"></td>}
                {hasReferralMarkup && <td className="p-1 text-center"></td>}
                {hasNoQuoteCharges && hasServiceCharge && (
                  <td className="p-1 text-center">
                    {finalTotal?.total
                      ? fixedDecimals(
                          parseFloat(finalTotal?.total) -
                            parseFloat(data?.service_charge ?? "0"),
                        )
                      : "-"}
                  </td>
                )}
                <td className="p-1 text-center">{finalTotal?.total ?? 0}</td>
              </tr>
            </>
          )}
        </tbody>
      </table>
      {hasReferralMarkup && (
        <small className="mb-[8px] block text-ui-text-muted">
          {t("invoice.referral.markup", { amount: referralMarkup.toFixed(2) })}
          {referralRate ? ` (${referralRate})` : ""}
          {referralReward
            ? t("invoice.referral.reward", {
                id: referralReward.id ?? "-",
                amount:
                  Math.abs(
                    Number(referralReward.amount || 0) - referralMarkup,
                  ) >= 0.01
                    ? ` (${Number(referralReward.amount || 0).toFixed(2)})`
                    : "",
                status: referralReward.status ?? "-",
                referrer:
                  referralReward.referrer?.username ??
                  t("invoice.referral.user", {
                    id: referralReward.referrer_id ?? "-",
                  }),
                code: referralReward.referral?.referral_code
                  ? ` (${referralReward.referral.referral_code})`
                  : "",
              })
            : t("invoice.referral.noReward")}
          {referralRewards.length > 1
            ? t("invoice.referral.rows", { n: referralRewards.length })
            : ""}
        </small>
      )}
    </div>
  );
};

export default ShipmentChargesTable;
